import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Calendar } from 'lucide-react';
import { SERVICES, CALENDLY_URL } from '../data/services';
import '../styles/ServiceDetailPage.css';

const ServiceDetailPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const service = SERVICES[slug];
  const investmentSectionRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = service.seoTitle;
    }
  }, [service]);

  // Show sticky CTA after scrolling past hero; hide when in-page CTA is visible
  useEffect(() => {
    if (!service) return;

    let inPageCtaVisible = false;
    let scrolledPastHero = false;

    const update = () => {
      setShowStickyCta(scrolledPastHero && !inPageCtaVisible);
    };

    const onScroll = () => {
      scrolledPastHero = window.scrollY > 500;
      update();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        inPageCtaVisible = entry.isIntersecting;
        update();
      },
      { threshold: 0.15 }
    );

    if (investmentSectionRef.current) observer.observe(investmentSectionRef.current);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [service]);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="landing-page cms-page">
      {/* Header */}
      <header className="cms-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="logo-container cms-logo-link" data-testid="service-detail-home-link">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png"
                alt="AfriCasa Logo"
                className="logo"
              />
            </Link>
            <Link to={`/#service-${slug}`} className="cms-back-link" data-testid="service-detail-back-link">
              <ArrowLeft size={18} />
              <span>BACK TO HOME</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="cms-hero">
        <div className="container mx-auto px-6">
          <div className="cms-hero-inner">
            <p className="cms-eyebrow">{service.eyebrow}</p>
            <h1 className="cms-page-title" data-testid="service-detail-title">{service.title}</h1>
            <div className="cms-hero-divider" />
            <p className="cms-intro">{service.intro}</p>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <section className="cms-section">
        <div className="container mx-auto px-6">
          <div className="cms-section-inner">
            <h2 className="cms-section-title">{service.title}</h2>

            {service.sections.map((sec, idx) => (
              <div
                key={idx}
                className={`cms-block ${sec.title === 'Engagement Length' ? 'cms-block-duration' : ''}`}
              >
                <h3 className="cms-block-title">{sec.title}</h3>
                {sec.kind === 'block' && (
                  <p className="cms-block-text">{sec.text}</p>
                )}
                {sec.kind === 'list' && (
                  <ul className="cms-list">
                    {sec.items.map((item, i) => (
                      <li key={i}>
                        <CheckCircle2 size={20} className="cms-list-icon" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment / Pricing — only shown when the service defines an investment block */}
      {service.investment && (
        <section
          className="cms-investment"
          ref={investmentSectionRef}
          data-testid="service-investment-section"
        >
          <div className="container mx-auto px-6">
            <div className="cms-investment-inner">
              <h2 className="cms-investment-title">Investment</h2>
              <div className="cms-investment-divider" />

              {service.investment.intro && (
                <p className="cms-investment-intro" data-testid="investment-intro">
                  {service.investment.intro}
                </p>
              )}

              {/* Custom-text variant — used on Organizational Consulting pages where
                  pricing is customized per engagement (no tier cards). */}
              {service.investment.text && (
                <div className="cms-investment-custom" data-testid="investment-custom">
                  <p className="cms-investment-custom-text">{service.investment.text}</p>
                  {service.investment.cta && (
                    <a
                      href={service.investment.cta.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold cms-investment-custom-cta"
                      data-testid="investment-custom-cta"
                    >
                      {service.investment.cta.label}
                      <ArrowRight size={16} />
                    </a>
                  )}
                </div>
              )}

              {service.investment.tiers && (
              <div
                className={`cms-pricing-grid${
                  service.investment.grid === 'two-column' ? ' cms-pricing-grid--two-col' : ''
                }`}
              >
                {service.investment.tiers.map((tier, i) => (
                  <div
                    key={i}
                    className={`cms-pricing-card${tier.popular ? ' cms-pricing-card--popular' : ''}`}
                    data-testid={`pricing-tier-${i}`}
                  >
                    {tier.popular && (
                      <span className="cms-pricing-popular-badge" data-testid={`pricing-tier-${i}-popular-badge`}>
                        Most Popular
                      </span>
                    )}
                    <h3 className="cms-pricing-name">{tier.name}</h3>
                    {tier.subtitle && <p className="cms-pricing-subtitle">{tier.subtitle}</p>}

                    {tier.price && (
                      <div className="cms-pricing-amount-block">
                        <span className="cms-pricing-amount">{tier.price}</span>
                        {tier.priceUnit && (
                          <span className="cms-pricing-unit">{tier.priceUnit}</span>
                        )}
                        {tier.savings && (
                          <span className="cms-pricing-tier-savings" data-testid={`pricing-tier-${i}-savings`}>
                            {tier.savings}
                          </span>
                        )}
                      </div>
                    )}

                    {tier.options && (
                      <ul className="cms-pricing-options">
                        {tier.options.map((opt, j) => (
                          <li key={j} className="cms-pricing-option">
                            <div className="cms-pricing-option-header">
                              <span className="cms-pricing-option-label">{opt.label}</span>
                              <span className="cms-pricing-option-price">
                                {opt.price}
                                {opt.priceUnit && (
                                  <span className="cms-pricing-option-unit"> {opt.priceUnit}</span>
                                )}
                              </span>
                            </div>
                            {opt.detail && (
                              <p className="cms-pricing-option-detail">{opt.detail}</p>
                            )}
                            {opt.savings && (
                              <span className="cms-pricing-option-savings">{opt.savings}</span>
                            )}
                            {opt.cta && (
                              <a
                                href={opt.cta.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cms-pricing-option-cta"
                                data-testid={`pricing-tier-${i}-option-${j}-cta`}
                              >
                                {opt.cta.label}
                                <ArrowRight size={14} />
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {tier.cta && (
                      <a
                        href={tier.cta.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-gold cms-pricing-cta"
                        data-testid={`pricing-tier-${i}-cta`}
                      >
                        {tier.cta.label}
                        <ArrowRight size={16} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
              )}

              {service.investment.note && (
                <p className="cms-pricing-note" data-testid="pricing-note">
                  {service.investment.note}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Sticky floating CTA — appears after scrolling past hero, hides when Investment section visible */}
      <a
        href={(service.ctaButton && service.ctaButton.url) || service.calendlyUrl || CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`sticky-cta-pill ${showStickyCta ? 'sticky-cta-pill-visible' : ''}`}
        data-testid="sticky-cta-pill"
        aria-label={(service.ctaButton && service.ctaButton.label) || 'Book a Consultation'}
      >
        <Calendar size={18} className="sticky-cta-icon" aria-hidden="true" />
        <span className="sticky-cta-label">{(service.ctaButton && service.ctaButton.label) || 'Book a Consultation'}</span>
      </a>
    </div>
  );
};

export default ServiceDetailPage;
